import React from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import HashTag from "../components/HashTag";
import {
  Story,
  StoryHero,
  StoryNav,
  StoryOutro,
  Section,
  Stat,
  StatRow,
  Card,
  Eq,
  Prose,
  Split,
  Bullets,
  Steps,
  fade,
} from "../components/story";
import {
  ChainDiagram,
  GeneratorDiagram,
  TimingDiagram,
  ImpairmentDiagram,
} from "../components/diagrams/NtnTestbedDiagrams";

const TAGS = [
  "NTN",
  "5GNR",
  "LEO",
  "OCUDU",
  "OpenAirInterface",
  "GNURadio",
  "ChannelModel",
  "Doppler",
  "SatelliteComms",
];

const NAV = [
  ["problem", "The problem"],
  ["chain", "The chain"],
  ["generator", "One geometry"],
  ["timing", "Timing contract"],
  ["channel", "Channel"],
  ["dsp", "The DSP"],
  ["limits", "Limits"],
  ["unlocks", "What it unlocks"],
];

/** The four reference geometries the rig ships with. */
const SCENARIOS = [
  ["LEO pass", "5.518 ms", "40 slots", "33.7 kHz", "ephemeris snapshot, 40.9° elevation"],
  ["GEO service link", "251.561 ms", "284 slots", "—", "station-kept satellite at 100°W"],
  ["Inclined GEO", "—", "—", "present", "inclined orbit, for a non-zero Doppler term"],
  ["GEO feeder link", "—", "—", "—", "transparent payload, non-zero ta-Common"],
];

/** Verification assertions from the host test suite, not numbers off a slide. */
const DSP = [
  ["Fractional-delay group delay error", "under 1e-3 samples, across all 256 phases"],
  ["Passband ripple, ±2.25 MHz", "about 0.002 dB measured, under 0.02 dB enforced"],
  ["Doppler frequency error", "under 1e-3 Hz, from 10 Hz to 40 kHz"],
  ["C/N accuracy", "within 0.05 dB, from 0 to 40 dB"],
  ["Timing advance agreement, on air", "UE derived 42302 samples, channel applied 42302.6"],
  ["Scenario consistency", "derived and applied delay held to 3 ns"],
];

export default function NrNtnTestbedStory() {
  return (
    <Story>
      <Helmet>
        <title>An End-to-End 5G NR NTN Testbed | Soham Desai</title>
        <meta
          name="description"
          content="A real OCUDU gNB, a real OpenAirInterface UE and a real 5G core talking through a LEO satellite channel built from orbit data in GNU Radio. One geometry generates SIB19, the UE position and the channel delay together, so the timing contract cannot silently break."
        />
        <meta property="og:title" content="An End-to-End 5G NR NTN Testbed on One Laptop" />
        <meta
          property="og:description"
          content="5G NR over a LEO satellite, without the satellite: OCUDU + OpenAirInterface + Open5GS + a GNU Radio NTN channel, with a timing contract that is generated rather than hand-tuned."
        />
      </Helmet>

      <StoryHero
        breadcrumb={{ to: "/projects/satellite-comms", label: "5G & NTN" }}
        kicker="5G NR NTN · Skylo · open-source RAN stack"
        title={
          <>
            A LEO satellite link that runs on{" "}
            <span className="text-accent">one laptop</span>
          </>
        }
        lead={
          <>
            A real 5G NR base station, a real UE and a real 5G core, talking to each other through
            a satellite channel built from actual orbit data. No satellite, no SDR, and no MATLAB
            licence in the loop. Every NTN feature that matters, SIB19, timing advance, Doppler
            pre-compensation, long round-trip time and scheduling offsets, exercised end to end
            against stock NR software you can stop, inspect, and change one variable at a time.
          </>
        }
        stats={[
          { value: "5.518", unit: "ms", label: "round trip on the reference LEO pass" },
          { value: "33.7", unit: "kHz", label: "peak Doppler the UE has to track" },
          { value: "40", label: "slots of K_offset the scheduler must respect", amber: true },
        ]}
        chips={[
          "OCUDU gNB · OAI UE · Open5GS core",
          "GNU Radio NTN channel",
          "Band n256 · 5 MHz FDD · 15 kHz SCS",
        ]}
      />

      <StoryNav items={NAV} />

      <div className="max-w-4xl mx-auto px-4 md:px-8">
        {/* ══════════ 1 · PROBLEM ══════════ */}
        <Section
          id="problem"
          kicker="Why · the problem"
          title="NTN rigs fail quietly"
          lead="Testing a non-terrestrial feature normally means a satellite, a five-figure channel emulator, or a commercial simulator with MATLAB in the run-time loop. All three are slow to get hold of and hard to instrument. But the real problem is not cost, it is that the usual way these rigs fail leaves no trace."
        >
          <Prose>
            <p>
              The failure looks like this. The emulator applies one delay. SIB19 advertises a
              slightly different one. The UE derives a timing advance from the advertised
              ephemeris, transmits, and its uplink lands in the wrong slot. The attach stalls, and
              there is nothing in any log that says why, because every component individually
              believes it is behaving correctly.
            </p>
            <p>
              You can spend days on that, and the usual resolution is somebody quietly adding a
              fudge factor until it works, which destroys the value of the measurement you were
              trying to take.
            </p>
          </Prose>

          <Card accent title="The question">
            <p>
              Not &ldquo;how do we emulate a satellite channel&rdquo;, but{" "}
              <em className="text-foreground not-italic font-medium">
                how do we make it structurally impossible for the channel and the broadcast
                ephemeris to disagree
              </em>
              , so that when something breaks, it is the thing under test?
            </p>
          </Card>
        </Section>

        {/* ══════════ 2 · THE CHAIN ══════════ */}
        <Section
          id="chain"
          kicker="The build"
          title="Five real components, one flowgraph"
          lead="Nothing here is a stub or a synthetic OFDM source. Every element is production or reference software, which is the point: the NR stack has to be surprised by the channel in the same way it would be by a real satellite."
        >
          <ChainDiagram />

          <Bullets
            items={[
              <>
                <strong className="text-foreground">
                  <a
                    href="https://ocudu.org/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent hover:text-accent-hover underline underline-offset-4"
                  >
                    OCUDU
                  </a>{" "}
                  as the gNB
                </strong>
                , the Linux Foundation&rsquo;s permissively-licensed open-source CU/DU with the
                full L1/L2/L3 stack, on band n256, 5 MHz FDD, 15 kHz subcarrier spacing,
                broadcasting SIB19.
              </>,
              <>
                <strong className="text-foreground">Open5GS</strong> as the 5G core, so the attach
                runs all the way through NAS registration to a real PDU session.
              </>,
              <>
                <strong className="text-foreground">
                  An OpenAirInterface <span className="font-mono">nr-uesoftmodem</span>
                </strong>{" "}
                as the UE, running the Release 17 SIB19 path so it derives its own service-link
                timing advance rather than being told one.
              </>,
              <>
                <strong className="text-foreground">A bidirectional GNU Radio NTN channel</strong>{" "}
                applying delay, Doppler, carrier-to-noise ratio and fading.
              </>,
              <>
                <strong className="text-foreground">A bridge</strong> translating between
                OCUDU&rsquo;s ZeroMQ radio interface and OAI&rsquo;s rfsimulator, which also owns
                the timing budget between the two.
              </>,
            ]}
          />

          <Card title="Why one flowgraph owns both directions">
            <p>
              A single GNU Radio flowgraph carries uplink and downlink, so the scheduler inside
              GNU Radio provides the lockstep between them rather than two independent processes
              drifting against wall-clock. The downlink channel is applied{" "}
              <em className="text-foreground not-italic font-medium">before</em> the UE sees the
              signal and the uplink channel <em className="text-foreground not-italic font-medium">after</em>{" "}
              it transmits, which is the only physically meaningful ordering and, awkwardly, not
              the one you get by default if you bolt two channels onto an existing rig.
            </p>
          </Card>
        </Section>

        {/* ══════════ 3 · GENERATOR ══════════ */}
        <Section
          id="generator"
          kicker="The idea"
          title="One geometry, every artifact"
          lead="This is the part that makes the rest trustworthy. Nothing in the configuration is written by hand. A single geometry, a LEO state vector or a GEO longitude, produces every artifact the run needs in one generator pass."
        >
          <GeneratorDiagram />

          <Steps
            items={[
              [
                "1",
                "Start from geometry",
                "One state vector or orbital longitude, propagated from a bundled ephemeris snapshot in pure Python. No MATLAB at run time.",
              ],
              [
                "2",
                "Emit everything together",
                "The gNB's NTN config, the SIB19 ephemeris it broadcasts, the UE's position file and the channel's delay and Doppler profile all fall out of the same run.",
              ],
              [
                "3",
                "Assert they agree",
                "A consistency test re-derives the UE's timing advance exactly the way OAI's own NTN layer does, and holds it against what the channel will apply.",
              ],
              [
                "4",
                "Never hand-edit",
                "The generated config files are only consistent because one run produced all of them. Editing one by hand silently breaks the guarantee.",
              ],
            ]}
          />

          <Prose>
            <p>
              The two are held to{" "}
              <strong className="text-foreground">3 nanoseconds</strong> of each other. That number
              is not a tolerance anyone chose, it is the quantisation floor: SIB19 carries
              ephemeris in roughly 1.3 m units, which at the speed of light is about a hundredth of
              a sample at this rate. In other words, the derived and applied delays agree as
              closely as the broadcast format can physically express.
            </p>
          </Prose>
        </Section>

        {/* ══════════ 4 · TIMING ══════════ */}
        <Section
          id="timing"
          kicker="The physics"
          title="Two conditions decide whether anything attaches"
          lead="Everything about whether a UE gets onto this network lives in two relations. If either fails, the symptom is a stalled attach with no informative log line anywhere in the chain."
        >
          <Eq>
            D<sub>dl</sub> + D<sub>ul</sub> = TA<sub>ue</sub>
            <span className="mx-4 text-muted">and</span>
            RTT ≤ K<sub>offset</sub> + k<sub>2</sub>
          </Eq>

          <TimingDiagram />

          <Prose>
            <p>
              The first says the uplink answer to a grant only lands in the slot the scheduler
              expects when the total transport delay equals the timing advance the UE applied. The
              second says the round trip has to fit inside the scheduling offset the base station
              advertised, or the grant expires before the answer can physically arrive.
            </p>
            <p>
              A Release 17 UE derives its service-link advance from the SIB19 ephemeris and its own
              position, then adds the broadcast common term. So when the scenario is physically
              consistent by construction, the residual offset is{" "}
              <strong className="text-foreground">zero</strong> and nothing needs fudging. That is
              the whole payoff of generating the artifacts together rather than configuring them
              separately.
            </p>
          </Prose>

          <motion.div variants={fade} className="overflow-x-auto">
            {/* Five columns will not fit a phone; let the wrapper scroll rather than
                squeeze every cell to a four-line column. */}
            <table className="w-full min-w-[34rem] text-sm border-collapse">
              <caption className="text-left text-xs text-muted-foreground pb-3">
                Four reference geometries ship ready to run. The LEO one regenerates from a bundled
                ephemeris snapshot.
              </caption>
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="py-2.5 pr-4 text-muted-foreground font-normal">Scenario</th>
                  <th className="py-2.5 pr-4 text-muted-foreground font-normal">Round trip</th>
                  <th className="py-2.5 pr-4 text-muted-foreground font-normal">K_offset</th>
                  <th className="py-2.5 pr-4 text-muted-foreground font-normal">Peak Doppler</th>
                  <th className="py-2.5 text-muted-foreground font-normal">Geometry from</th>
                </tr>
              </thead>
              <tbody className="text-muted">
                {SCENARIOS.map(([name, rtt, koff, dop, geom]) => (
                  <tr key={name} className="border-b border-border/50">
                    <td className="py-2.5 pr-4 text-foreground">{name}</td>
                    <td className="py-2.5 pr-4 font-mono text-xs">{rtt}</td>
                    <td className="py-2.5 pr-4 font-mono text-xs">{koff}</td>
                    <td className="py-2.5 pr-4 font-mono text-xs">{dop}</td>
                    <td className="py-2.5 text-xs">{geom}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        </Section>

        {/* ══════════ 5 · CHANNEL ══════════ */}
        <Section
          id="channel"
          kicker="Impairments"
          title="Where the satellite is, and what else is in the way"
          lead="The geometry profile answers the first question. A separate, layered channel config answers the second, and it is deliberately incapable of moving a delay or a Doppler, so the timing contract stays provably untouched no matter how hard you sweep the impairments."
        >
          <Split>
            <div className="space-y-5">
              <Prose>
                <p className="font-display text-xl font-semibold text-foreground">
                  Layers that merge, not replace
                </p>
                <p>
                  Config layers merge in argument order, later layers winning field by field, so a
                  fading profile and a noise offset compose rather than conflict. With no layer at
                  all the channel is bit-for-bit what it was before the impairment feature existed,
                  and a test asserts exactly that, so adding the capability cannot retroactively
                  move a historical measurement.
                </p>
              </Prose>
              <Bullets
                items={[
                  "NTN-TDL A/B/C/D from the 3GPP NTN channel models, plus custom TDL definitions.",
                  "Externally generated MATLAB traces, if you want a specific realisation.",
                  "Lognormal shadowing, and C/N offsets or absolute overrides.",
                  "CW, AWGN, filtered-AWGN or pulsed interferers, placed by I/N or C/I, on either direction, with start and stop times on the pass clock.",
                ]}
              />
            </div>

            <div className="space-y-5">
              <Card accent title="Ordering is physics, not preference">
                <p>
                  Fading and shadowing <em className="text-foreground not-italic font-medium">multiply</em>{" "}
                  the wanted signal, so they have to precede the Doppler rotation. Interference and
                  thermal noise <em className="text-foreground not-italic font-medium">add</em> at
                  the receiver front end, so they have to follow it. Getting this backwards
                  produces a channel that looks plausible and is wrong.
                </p>
              </Card>
              <Card title="One interpolator, whatever the profile">
                <p>
                  Each fading tap&rsquo;s total delay selects its own row of the fractional-delay
                  bank, and the rows are summed into a single composite kernel. A faded stream
                  therefore passes through exactly one interpolator rather than a cascade, which is
                  what preserves the passband flatness measured below even under the most
                  dispersive profiles.
                </p>
              </Card>
            </div>
          </Split>

          <ImpairmentDiagram />
        </Section>

        {/* ══════════ 6 · DSP ══════════ */}
        <Section
          id="dsp"
          kicker="Verification"
          title="What the signal processing is actually measured at"
          lead="A channel emulator is only worth as much as its own error budget, so the DSP properties are assertions in a host test suite rather than claims on a slide. The suite runs on a bare Linux box with no GNU Radio, gNB, UE, core or radio present."
        >
          <motion.div variants={fade} className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="py-2.5 pr-4 text-muted-foreground font-normal">Property</th>
                  <th className="py-2.5 text-muted-foreground font-normal">Result</th>
                </tr>
              </thead>
              <tbody className="text-muted">
                {DSP.map(([prop, result]) => (
                  <tr key={prop} className="border-b border-border/50">
                    <td className="py-2.5 pr-4 text-foreground">{prop}</td>
                    <td className="py-2.5 font-mono text-xs">{result}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>

          <StatRow cols={3}>
            <Stat value="112" label="host tests, no radio or RAN software required" />
            <Stat value="256" label="fractional-delay filter phases, all verified" />
            <Stat value="0.002" unit="dB" label="measured passband ripple across ±2.25 MHz" amber />
          </StatRow>

          <Prose>
            <p className="text-sm text-muted-foreground">
              The timing-advance row is the one worth dwelling on, because it is the end-to-end
              check rather than a component one. The UE independently derived 42302 samples of
              advance from the broadcast ephemeris; the channel independently applied 42302.6. Two
              separate code paths, from the same geometry, agreeing to within a sample on air.
            </p>
          </Prose>
        </Section>

        {/* ══════════ 7 · LIMITS ══════════ */}
        <Section
          id="limits"
          kicker="Honesty · what this does not do"
          title="Four things that will bite you"
          lead="All four are understood and documented. None of them stops the rig being useful, and all of them will quietly corrupt a measurement if you do not know about them going in."
        >
          <Card>
            <Bullets
              items={[
                <>
                  <strong className="text-foreground">It runs at about a tenth of real time.</strong>{" "}
                  Three Python blocks contend for one interpreter lock, delivering roughly 1 MS/s
                  against the 7.68 MS/s the profile wants. Every wall-clock timer in the gNB and
                  core is therefore effectively ten times too short. A config layer scales the ones
                  reachable from configuration; the core&rsquo;s NAS timers are a compile-time
                  table and are not.
                </>,
                <>
                  <strong className="text-foreground">SIB19 is broadcast once and never refreshed.</strong>{" "}
                  A single anchored snapshot tracks the moving channel to under a cyclic prefix for
                  about a minute, and to a couple of cyclic prefixes across a full pass. The UE
                  re-reads SIB19 at half the advertised validity, so eventually the epoch flips and
                  the downlink stops.
                </>,
                <>
                  <strong className="text-foreground">The scheduling request is never detected.</strong>{" "}
                  Real HARQ feedback on the same PUCCH format in the same seconds decodes fine, but
                  the SR detection metric never clears threshold, so the uplink effectively runs on
                  the base station&rsquo;s PUCCH false-alarm rate. That stretches the attach
                  considerably.
                </>,
                <>
                  <strong className="text-foreground">The UE never builds its TUN interface.</strong>{" "}
                  The radio bearer comes up and the core allocates an address, but the UE latches
                  its NAS security state early, a downstream consequence of the slow uplink above,
                  and discards later downlink NAS messages. Downlink air-interface traffic is
                  measurable; uplink throughput is not.
                </>,
              ]}
            />
          </Card>

          <Prose>
            <p className="text-sm text-muted-foreground">
              Throughput, BLER and attach-timing figures from the recorded campaign are not
              reproduced here. This write-up covers the architecture, the method and the
              verification of the emulator itself.
            </p>
          </Prose>
        </Section>

        {/* ══════════ 8 · UNLOCKS ══════════ */}
        <Section
          id="unlocks"
          kicker="What it is for"
          title="Real NR I/Q with the ground truth attached"
          lead="The reason to build this rather than buy an emulator is that it hands you standards-compliant NR samples with known truth beside them: geometry, ephemeris, channel state and network events, all off the same sample clock. Capture at two points and you have a matched pair to work against offline."
        >
          <motion.div variants={fade} className="grid gap-4 sm:grid-cols-2">
            {[
              [
                "Synchronisation and channel estimation",
                "Real SSB, PRACH and DMRS through a moving channel with tens of kHz of Doppler, against a geometry you already know exactly.",
              ],
              [
                "Interference mitigation",
                "CW, pulsed and adjacent-channel emitters, placed by I/N or C/I on either direction, with start and stop times on the pass clock.",
              ],
              [
                "Pre-flight NTN validation",
                "Exercise SIB19, timing advance, K_offset and Doppler pre-compensation before anything touches RF, a satellite, or a regulator.",
              ],
              [
                "ISAC and geolocation",
                "A known waveform, a known geometry and captured I/Q, which is exactly the raw material for delay/Doppler sensing and TDOA/FDOA work.",
              ],
              [
                "Scheduler and MAC research",
                "A 40-slot scheduling offset and a 16-process HARQ cycle against a 5.5 ms round trip, all of it observable in the base-station log.",
              ],
              [
                "Regression",
                "Pinned upstream commits, a host test suite, automatic KPI extraction and archived logs, so a change that moves a number is visible immediately.",
              ],
            ].map(([title, body]) => (
              <div
                key={title}
                className="rounded-lg border border-border bg-surface/50 p-5 hover:border-accent/30 transition-colors"
              >
                <h3 className="font-display text-base font-semibold text-foreground mb-2">
                  {title}
                </h3>
                <p className="text-sm text-muted leading-relaxed">{body}</p>
              </div>
            ))}
          </motion.div>
        </Section>

        <StoryOutro
          title="The satellite is a GNU Radio block"
          tags={TAGS}
          TagComponent={HashTag}
          backTo="/projects/satellite-comms"
          backLabel="More from 5G & NTN"
        >
          <p className="mt-4 text-lg">
            The orbit is a state vector, the channel is a flowgraph, and the base station, UE and
            core are the same software that runs on real networks. Because one generator produces
            the ephemeris the network broadcasts and the delay the channel applies, the two cannot
            silently disagree, which removes the single most common way a non-terrestrial testbed
            wastes a week.
          </p>
          <p>
            What is left is a rig where you can change exactly one variable, re-run, and believe
            the difference.
          </p>
        </StoryOutro>
      </div>
    </Story>
  );
}
